/** @jsxImportSource @emotion/react */
import { Interpolation, Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { forwardRef, useState } from "react";
import { theme } from "styles/theme";
import Icon from "uis/Icon";

type ButtonType = "primary" | "secondary" | "disabled" | "destructive";
type ButtonProps = {
    buttonClassName?: string;
    optionsClassName?: string;
    onClick?: () => void;
    onClickOption?:() => void;
    type?: ButtonType;
    buttonStyles?: {
        text?: Interpolation<Theme>;
        container?: Interpolation<Theme>;
    };
    optionStyles?: {
        container?: Interpolation<Theme>;
    };
    title?: string;
    leftIcon?: ({ color, width, height }: { color?: string; width?: string; height?: string }) => JSX.Element;
    rightIcon?: ({ color, width, height }: { color?: string; width?: string; height?: string }) => JSX.Element;
};

const RootContainer = styled.div`
    display: flex;
    flex-direction: row;
`;

const ButtonContainer = styled.div<{ type: "primary" | "secondary" | "disabled" | "destructive" }>`
    border-radius: 8px 0px 0px 8px;
    justify-content: center;
    align-items: center;
    gap: 5px;
    display: flex;
    background-color: ${(props) =>
        props.type === "primary" ? theme.primary500 : props.type === "secondary" ? theme.primary50 : "#FFF6F6"};
    color: ${(props) => (props.type === "primary" ? "white" : props.type === "secondary" ? theme.primary500 : "#FF3E3E")};
    cursor: pointer;
    &:hover {
        background-color: ${(props) => (props.type === "primary" ? "#1065ED" : props.type === "secondary" ? theme.primary500 : "#FFDFDF")};
        color: ${(props) => props.type !== "destructive" && "white"};
    }
`;

const ButtonTitle = styled.p`
    font-weight: 700;
    font-size: 16px;
`;

const OptionButtonContainer = styled.div<{ type: "primary" | "secondary" | "disabled" | "destructive" }>`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;

    width: 60px;
    
    border-radius: 0px 8px 8px 0px;
    background-color: ${(props) =>
        props.type === "primary" ? theme.primary500 : props.type === "secondary" ? theme.primary50 : "#FFF6F6"};
    color: ${(props) => (props.type === "primary" ? "white" : props.type === "secondary" ? theme.primary500 : "#FF3E3E")};
    cursor: pointer;
    &:hover {
        background-color: ${(props) => (props.type === "primary" ? "#1065ED" : props.type === "secondary" ? theme.primary500 : "#FFDFDF")};
        color: ${(props) => props.type !== "destructive" && "white"};
    }
`;

const OptionButton = forwardRef<HTMLDivElement, ButtonProps>((props: ButtonProps, buttonRef) => {
    const { type = "primary", buttonStyles, optionStyles, title, leftIcon, rightIcon, onClick = () => {}, onClickOption = () => {}, buttonClassName, optionsClassName } = props;
    const [hover, setHover] = useState(false);
    
    return (
        <RootContainer ref={buttonRef}>
            <ButtonContainer
                className={buttonClassName}
                type={type}
                css={buttonStyles?.container}
                onClick={onClick}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                {leftIcon &&
                    leftIcon({
                        color: hover ? "white" : type === "primary" ? "white" : props.type === "secondary" ? theme.primary500 : "#FF3E3E",
                    })}
                {title ? <ButtonTitle css={buttonStyles?.text}>{title}</ButtonTitle> : null}
                {rightIcon &&
                    rightIcon({
                        color: hover ? "white" : type === "primary" ? "white" : props.type === "secondary" ? theme.primary500 : "#FF3E3E",
                    })}
            </ButtonContainer>
            <OptionButtonContainer 
                className={optionsClassName}
                type={type}
                css={optionStyles?.container}
                onClick={onClickOption}
            >
                <Icon className={optionsClassName} name="top-arrow" color="white" width={24} height={24}/>
            </OptionButtonContainer>
        </RootContainer>
    );
});

export default OptionButton;
